import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery } from '@tanstack/react-query';

import { apiLogger } from '~/app';
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  DeleteSubscriptionRequest,
  SubscriptionOverview,
} from '~/server/src/dtos';

import { deleteFn, getFn, MessageResponse, postFn, queryClient, RequestError } from './api';
import { account } from './apiAccount';
import { useFirebaseAnalytics } from './useFirebase';

export const payment = createQueryKeys('payment', {
  initClient: null,
});

export const getPaymentSubscriptionOverview = `/payment/subscriptionOverview`;
export const postPaymentCreateSubscription = `/payment/subscriptions`;
export const postPaymentDeleteSubscription = `/payment/subscriptions`;

export const usePaymentInitClient = () => {
  return useQuery<SubscriptionOverview, RequestError>({
    queryKey: payment.initClient.queryKey,
    queryFn: ({ signal }) =>
      getFn(getPaymentSubscriptionOverview, undefined, undefined, { signal }),
  });
};

// Real Mollie subscription creation
export const useCreateSubscription = () => {
  const analytics = useFirebaseAnalytics();
  return useMutation<CreateSubscriptionResponse, RequestError, CreateSubscriptionRequest>({
    mutationFn: async (data) => {
      return postFn(postPaymentCreateSubscription, data);
    },
    retry: (failureCount, error) => {
      // Don't retry on 500 internal server errors for payment subscriptions
      if (error.status === 500) {
        apiLogger.debug(
          'blocked retry for "500 Internal Server Error" on payment subscription creation',
        );
        return false;
      }
      // Use default retry logic for other errors
      if (error.status === 400) {
        apiLogger.debug('blocked retry for "400 Bad Request"');
        return false;
      } else if (error.status === 401) {
        apiLogger.debug('blocked retry for "401 Unauthorized"');
        return false;
      } else if (error.status === 404) {
        apiLogger.debug('blocked retry for "404 Not Found"');
        return false;
      }
      if (failureCount <= 3) {
        apiLogger.debug({ failureCount }, 'retrying');
        return true;
      }
      apiLogger.debug('stop retrying');
      return false;
    },
    onSuccess: (_data, variables) => {
      queryClient.resetQueries({ queryKey: payment.initClient.queryKey });
      queryClient.resetQueries({ queryKey: account.detail.queryKey });
      analytics?.logEvent('purchase_subscription', {
        subscription: variables.subscription,
      });
    },
    onError: (error, variables) => {
      queryClient.resetQueries({ queryKey: payment.initClient.queryKey });
      analytics?.logEvent('exception', {
        description: `purchase_subscription failed: ${error.message}`,
        subscription: variables.subscription,
      });
    },
  });
};

export const useDeleteSubscription = () =>
  useMutation<MessageResponse, RequestError, DeleteSubscriptionRequest>({
    mutationFn: (data) => deleteFn(postPaymentDeleteSubscription, data),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: payment.initClient.queryKey });
      queryClient.resetQueries({ queryKey: account.detail.queryKey });
    },
  });

interface UiStubSubscriptionActionResponse {
  message: string;
}

// TODO: Replace UI stub with real purchase API flow once backend purchase finalization is delivered.
export const useCreateSubscriptionUiStub = () =>
  useMutation<UiStubSubscriptionActionResponse, never, CreateSubscriptionRequest>({
    mutationFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return {
        message: 'Subscription purchase action is pending backend delivery.',
      };
    },
  });

// TODO: Replace UI stub with real cancellation API flow once backend cancellation endpoint is delivered.
export const useDeleteSubscriptionUiStub = () =>
  useMutation<UiStubSubscriptionActionResponse, never, DeleteSubscriptionRequest>({
    mutationFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return {
        message: 'Subscription cancellation action is pending backend delivery.',
      };
    },
  });
